<script setup>
import { onMounted, ref, watch, onBeforeUnmount, nextTick, computed } from 'vue'
import { showToast, Dialog } from 'vant'
import indexedDB from '@/utils/indexedDB'
import {
  sendMessageVote,
  sendMessageRecord,
  editMessage,
  getImageMediaIds,
  sendDefalutStyle,
  getDefalutStyle,
} from '@/api/chat'
import { sendChatMessage as wecomSendChatMessage } from '@/utils/wecom'
import NewsControl from './NewsControl.vue'

// 接收父组件传递的 props：初始消息和加载状态
const props = defineProps({
  initialMessages: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    // 新增：接收父组件的加载状态
    type: Boolean,
    default: false,
  },
})

// 定义需要向父组件传递的事件
const emit = defineEmits(['update-messages', 'refresh-reply'])

// 组件内部状态
const conversationMessages = ref([...props.initialMessages])
const loadingMessages = ref([
  '🪄 正在理解顾客的问题…',
  '📚 查查总部的知识库，确认下最准确的说法～',
  '💬 帮您组织成一句好用的话术中…',
  '✅ 推荐话术准备好了，请查收～',
])
const currentLoadingMessage = ref(0)
let loadingInterval = null
// 控制建议输入框显示的状态
const showAdviceInput = ref({})
const adviceInputs = ref({})
// 控制版本切换的状态，key为消息索引，value为当前选择的版本（1=详细版，2=简化版）
const currentVersion = ref({})
// 全局默认风格
const globalDefaultStyle = ref(null)
// 控制外部提示框显示状态
const isTooltipVisible = ref(false)
const normalizeStyle = (style) => {
  const normalized = Number(style)
  return normalized === 2 ? 2 : 1
}

const getSelectedStyle = (message, index) => {
  const messageStyle = message?.default_style
  if (messageStyle !== null && messageStyle !== undefined) {
    return normalizeStyle(messageStyle)
  }
  if (currentVersion.value[index] !== undefined) {
    return normalizeStyle(currentVersion.value[index])
  }
  if (globalDefaultStyle.value !== null && globalDefaultStyle.value !== undefined) {
    return normalizeStyle(globalDefaultStyle.value)
  }
  return 1
}
// 获取当前版本的内容
const getCurrentVersionContent = (message, index) => {
  // 优先使用default_style，如果没有则使用currentVersion或默认值1
  const version = getSelectedStyle(message, index)
  if (message.style && Array.isArray(message.style)) {
    const styleContent = message.style.find((item) => Number(item.style) === Number(version))
    if (styleContent) {
      return styleContent.content || ''
    }
  }
  return message.content
}

// 计算是否有消息正在生成
const isAnyMessageGenerating = computed(() => {
  return conversationMessages.value.some((message) => {
    // 检查顶层isGenerating状态
    if (message.isGenerating) return true

    // 检查style数组中的isGenerating状态
    if (message.style && Array.isArray(message.style)) {
      return message.style.some((styleItem) => styleItem.isGenerating)
    }

    return false
  })
})

// 检查当前版本是否正在生成
const isCurrentVersionGenerating = (message, index) => {
  const version = getSelectedStyle(message, index)
  if (message.style && Array.isArray(message.style)) {
    const styleContent = message.style.find((item) => Number(item.style) === Number(version))
    if (styleContent) {
      return styleContent.isGenerating || false
    }
  }
  return message.isGenerating || false
}
// 滚动到最新助手消息顶部
const scrollToLatestAssistantMessage = () => {
  console.log('🔄 触发滚动到最新助手消息')
  nextTick(() => {
    console.log('🔄 执行滚动操作')
    // 尝试多种滚动容器选择器
    const containers = [
      document.querySelector('.content-wrapper'),
      document.querySelector('.conversation-content'),
      document.querySelector('.chat-container'),
    ]

    let container = null
    for (const c of containers) {
      if (c) {
        container = c
        break
      }
    }
    if (container) {
      const assistantMessages = container.querySelectorAll('.assistant-message')
      if (assistantMessages.length > 0) {
        const lastMessage = assistantMessages[assistantMessages.length - 1]
        // 计算滚动位置，确保版本切换按钮完全可见
        const rect = lastMessage.getBoundingClientRect()
        const scrollOffset = 100 // 额外滚动的像素值，确保版本切换按钮完全可见

        // 计算需要滚动的位置
        const targetScrollTop = container.scrollTop + rect.top - scrollOffset

        // 平滑滚动到目标位置
        container.scrollTo({
          top: targetScrollTop,
          behavior: 'smooth',
        })
      }
    }
  })
}

// 切换版本的方法
const switchVersion = (index, version) => {
  currentVersion.value[index] = version

  // 获取对应的消息
  const message = conversationMessages.value[index]
  if (!message || message.type !== 'assistant') {
    return
  }
  message.default_style = normalizeStyle(version)

  // 首先尝试从message.style中获取对应版本的内容
  if (message.style && Array.isArray(message.style)) {
    const styleContent = message.style.find((item) => item.style === version)
    if (styleContent && styleContent.content) {
      // 更新消息内容和投票状态
      conversationMessages.value[index] = {
        ...conversationMessages.value[index],
        content: styleContent.content,
        record_id: styleContent.record_id || message.record_id,
        vote: styleContent.vote,
        advice: styleContent.advice,
        hasVoted: styleContent.hasVoted || false,
        voteType: styleContent.voteType || styleContent.vote || 0,
      }
      conversationMessages.value = [...conversationMessages.value]
      // 调用sendDefalutStyle：payload.target_style 为所选风格，payload.record_id 为该风格对应记录
      const payloadRecordId = styleContent.record_id || message.record_id
      sendDefaultStyleToBackend(index, version, payloadRecordId)
      return
    }
  }

  // 如果message.style中没有找到，尝试从IndexedDB中查询
  const userId = localStorage.getItem('wecom_external_userid')
  if (!userId) {
    console.error('未找到用户ID')
    return
  }

  const fetchContentFromDB = async () => {
    try {
      // 从IndexedDB获取所有聊天记录
      const historyRecords = await indexedDB.getUserChatRecords(userId)

      // 查找对应的记录
      const recordIndex = Math.floor(index / 2) // 假设每两条消息（用户+AI）对应一条记录

      if (historyRecords && historyRecords.length > recordIndex) {
        const record = historyRecords[recordIndex]

        if (record && record.style && Array.isArray(record.style)) {
          // 查找对应版本的内容
          const styleContent = record.style.find((item) => item.style === version)
          if (styleContent && styleContent.content) {
            // 更新消息内容
            // conversationMessages.value[index].content = styleContent.content
            // 更新消息内容和投票状态
            conversationMessages.value[index] = {
              ...conversationMessages.value[index],
              content: styleContent.content,
              record_id: styleContent.record_id || message.record_id,
              vote: styleContent.vote,
              advice: styleContent.advice,
              hasVoted: styleContent.hasVoted || false,
              voteType: styleContent.voteType || styleContent.vote || 0,
            }
            conversationMessages.value = [...conversationMessages.value]
            const payloadRecordId = styleContent.record_id || message.record_id
            sendDefaultStyleToBackend(index, version, payloadRecordId)
          }
        }
      }
    } catch (error) {
      console.error('切换版本失败:', error)
    }
  }
  fetchContentFromDB()
}

// 调用sendDefalutStyle：请求体含 default_style 与 payload { target_style, record_id }
const sendDefaultStyleToBackend = async (index, style, recordIdFromSwitch) => {
  try {
    // 获取userId
    const userId = localStorage.getItem('wecom_external_userid')
    if (!userId) {
      throw new Error('未找到用户ID')
    }

    // 获取当前消息
    const message = conversationMessages.value[index]

    // 尝试从message中获取uuid
    let uuid = message.uuid
    if (!uuid) {
      // 从IndexedDB获取uuid
      const historyRecords = await indexedDB.getUserChatRecords(userId)
      const recordIndex = Math.floor(index / 2)
      if (
        historyRecords &&
        historyRecords.length > recordIndex &&
        historyRecords[recordIndex].uuid
      ) {
        uuid = historyRecords[recordIndex].uuid
      }
    }
    if (uuid) {
      const recordId = recordIdFromSwitch ?? message.record_id
      await sendDefalutStyle(userId, uuid, style, recordId)
      // 更新IndexedDB中的记录，设置default_style
      const historyRecords = await indexedDB.getUserChatRecords(userId)
      const recordIndex = Math.floor(index / 2)
      if (historyRecords && historyRecords.length > recordIndex) {
        const record = historyRecords[recordIndex]
        await indexedDB.updateChatRecordByRecordId(record.record_id, {
          default_style: style,
        })
        // 更新内存中的消息对象，添加default_style字段
        conversationMessages.value[index] = {
          ...conversationMessages.value[index],
          default_style: style,
        }
        conversationMessages.value = [...conversationMessages.value]
      }
      console.log('设置默认话术风格成功')
    }
  } catch (error) {
    console.error('设置默认话术风格失败:', error)
  }
}

// 控制编辑弹出框的状态
const showEditDialog = ref(false)
const currentEditingMessage = ref({})
const editingContent = ref('')
const currentEditingMediaSuffix = ref('')

// 监听父组件传递的加载状态变化，控制加载提示动画
watch(
  () => props.isLoading,
  (newVal) => {
    // 无论什么情况，先清除可能存在的旧定时器
    if (loadingInterval) {
      clearInterval(loadingInterval)
      loadingInterval = null
    }

    if (newVal) {
      // 重置当前加载消息索引
      currentLoadingMessage.value = 0
      // 启动加载提示语轮播
      loadingInterval = setInterval(() => {
        currentLoadingMessage.value =
          (currentLoadingMessage.value + 1) % loadingMessages.value.length
      }, 1500)
    } else {
      // 结束加载：重置消息索引
      currentLoadingMessage.value = 0
    }
  },
  // 添加immediate选项，确保初始状态也能正确处理
  { immediate: true },
)

// 监听对话信息变化，不再自动滚动到底部（由父容器控制滚动）
watch(
  () => conversationMessages,
  () => {
    // 滚动逻辑已移除，由父容器处理
  },
  { deep: true },
)

/** 是否存在流式生成中的消息（父组件每个 chunk 都会触发 initialMessages 更新） */
const hasStreamingMessages = (messages) =>
  Array.isArray(messages) &&
  messages.some(
    (message) =>
      message.isGenerating === true ||
      (Array.isArray(message.style) && message.style.some((s) => s.isGenerating === true)),
  )

/** 防止 initialMessages 异步 IndexedDB 回写覆盖更新的流式内容 */
let initialMessagesSyncGen = 0

// 监听父组件传递的初始消息变化
watch(
  () => props.initialMessages,
  async (newMessages) => {
    if (newMessages && Array.isArray(newMessages)) {
      const syncGen = ++initialMessagesSyncGen

      // 流式输出时仅同步消息，不查 IndexedDB，避免竞态导致内容回跳与页面抖动
      if (hasStreamingMessages(newMessages)) {
        conversationMessages.value = [...newMessages]
        return
      }

      conversationMessages.value = [...newMessages]
      // 从IndexedDB获取记录，添加default_style字段
      const userId = localStorage.getItem('wecom_external_userid')
      let historyRecords = []
      if (userId) {
        try {
          historyRecords = await indexedDB.getUserChatRecords(userId)
        } catch (error) {
          console.error('从IndexedDB获取记录失败:', error)
        }
      }

      if (syncGen !== initialMessagesSyncGen) return

      // 为消息添加default_style字段
      const messagesWithDefaultStyle = newMessages.map((message, index) => {
        if (message.type === 'assistant' && historyRecords.length > 0) {
          // 查找对应的记录
          const recordIndex = Math.floor(index / 2) // 假设每两条消息（用户+AI）对应一条记录
          if (historyRecords.length > recordIndex) {
            const record = historyRecords[recordIndex]
            if (record && record.default_style !== null && record.default_style !== undefined) {
              return {
                ...message,
                default_style: normalizeStyle(record.default_style),
              }
            }
          }
        }
        return message
      })

      if (syncGen !== initialMessagesSyncGen) return

      conversationMessages.value = messagesWithDefaultStyle
      // 初始化showAdviceInput和adviceInputs状态，根据历史消息中的advice字段
      messagesWithDefaultStyle.forEach((message, index) => {
        if (message.type === 'assistant' && message.advice) {
          showAdviceInput.value[index] = false
          adviceInputs.value[index] = message.advice
        }

        // 如果消息已带default_style，优先与currentVersion保持一致
        if (message.default_style !== null && message.default_style !== undefined) {
          currentVersion.value[index] = normalizeStyle(message.default_style)
        } else if (currentVersion.value[index] === undefined) {
          currentVersion.value[index] = getSelectedStyle(message, index)
        }
      })
    }
  },
  { deep: true, immediate: true },
)

// 新增：监听showAdviceInput变化，触发滚动
watch(
  () => showAdviceInput.value,
  (newVal, oldVal) => {
    // 遍历所有消息索引，判断是否有输入框从隐藏变为显示
    Object.entries(newVal).forEach(([index, isShow]) => {
      // 只有当“当前输入框显示”且“之前未显示”时才滚动（避免重复触发）
      if (isShow && oldVal[index] !== true) {
        scrollToBottom()
      }
    })
  },
  { deep: true },
)

// 新增：滚动到父容器底部（直接获取父组件的.content-wrapper）
const scrollToBottom = () => {
  nextTick(() => {
    // 直接获取父组件的滚动容器（父组件中定义的.content-wrapper）
    const container = document.querySelector('.content-wrapper')
    if (container) {
      // 滚动到底部（兼容不同浏览器）
      container.scrollTop = container.scrollHeight
    }
  })
}

// 重新生成回复
const handleRefresh = async (index) => {
  //获取当前选中的style，优先使用default_style，否则使用currentVersion或默认值1
  const message = conversationMessages.value[index]
  const currentStyle = getSelectedStyle(message, index)
  // 标记当前消息为生成中
  conversationMessages.value[index].isGenerating = true
  showAdviceInput.value[index] = false

  // 重置对应版本的内容为空，以显示加载状态
  if (
    conversationMessages.value[index].style &&
    Array.isArray(conversationMessages.value[index].style)
  ) {
    conversationMessages.value[index].style = conversationMessages.value[index].style.map(
      (styleItem) => {
        if (Number(styleItem.style) === Number(currentStyle)) {
          return {
            ...styleItem,
            content: '',
            isGenerating: true,
            vote: 0,
            advice: null,
            hasVoted: false,
            voteType: 0,
          }
        }
        return styleItem
      },
    )
    // 同时更新顶层content为空，确保默认显示加载状态
    conversationMessages.value[index].content = ''
    // 重置顶层投票状态为当前style的状态
    const currentStyleContent = conversationMessages.value[index].style.find(
      (item) => Number(item.style) === Number(currentStyle),
    )
    if (currentStyleContent) {
      conversationMessages.value[index].voteType = currentStyleContent.voteType || 0
      conversationMessages.value[index].advice = currentStyleContent.advice || null
      conversationMessages.value[index].hasVoted = currentStyleContent.hasVoted || false
    }
    conversationMessages.value = [...conversationMessages.value]
  } else {
    // 如果没有style数组，只重置顶层状态
    conversationMessages.value[index].content = ''
    conversationMessages.value[index].voteType = 0
    conversationMessages.value[index].advice = null
    conversationMessages.value[index].hasVoted = false
    conversationMessages.value = [...conversationMessages.value]
  }

  try {
    // 获取userId
    const userId = localStorage.getItem('wecom_external_userid')

    if (!userId) {
      throw new Error('未找到用户ID')
    }
    // 获取当前消息
    const message = conversationMessages.value[index]

    // 尝试从message.style中获取对应style的record_id
    let recordId = message.record_id
    if (message.style && Array.isArray(message.style)) {
      // 首先查找对应currentStyle的record_id
      const currentStyleContent = message.style.find(
        (item) => Number(item.style) === Number(currentStyle),
      )
      if (currentStyleContent && currentStyleContent.record_id) {
        recordId = currentStyleContent.record_id
      } else {
        // 如果找不到对应style的record_id，再尝试查找style=1的record_id
        const originalStyleContent = message.style.find((item) => Number(item.style) === 1)
        if (originalStyleContent && originalStyleContent.record_id) {
          recordId = originalStyleContent.record_id
        }
      }
    }

    // 如果仍然没有找到record_id，尝试从IndexedDB中获取
    if (!recordId) {
      try {
        const historyRecords = await indexedDB.getUserChatRecords(userId)
        const recordIndex = Math.floor(index / 2)
        if (
          historyRecords &&
          historyRecords.length > recordIndex &&
          historyRecords[recordIndex].record_id
        ) {
          recordId = historyRecords[recordIndex].record_id
        }
      } catch (error) {
        console.error('从IndexedDB获取record_id失败:', error)
      }
    }

    if (recordId) {
      const historyRecords = await indexedDB.getUserChatRecords(userId)
      const recordIndex = Math.floor(index / 2)
      const record = historyRecords[recordIndex]

      // 更新对应style的投票状态
      if (record && record.style && Array.isArray(record.style)) {
        record.style = record.style.map((styleItem) => {
          if (Number(styleItem.style) === Number(currentStyle)) {
            return {
              ...styleItem,
              vote: 0,
              advice: null,
              hasVoted: false,
              voteType: 0,
            }
          }
          return styleItem
        })

        // 更新到IndexedDB
        await indexedDB.updateChatRecordByRecordId(recordId, {
          style: record.style,
        })
      }

      // 直接通知父组件使用对应style的record_id和userId重新生成回复
      emit('refresh-reply', {
        index,
        content: conversationMessages.value[index].content,
        record_id: recordId,
        user_id: userId,
        style: currentStyle,
      })
    } else {
      console.error('未找到对应的style record_id')
      // 降级方案：通知父组件使用原来的方式
      emit('refresh-reply', {
        index,
        content: conversationMessages.value[index].content,
        style: currentStyle,
      })
    }
  } catch (error) {
    console.error('获取历史记录失败:', error)
    // 降级方案：通知父组件使用原来的方式
    emit('refresh-reply', {
      index,
      content: conversationMessages.value[index].content,
      style: currentVersion.value[index] || 1,
    })
  }
}

// 处理投票
const handleVote = async (index, vote) => {
  // 1. 立即更新UI状态
  if (vote === 0) {
    // 取消选择：重置状态
    conversationMessages.value[index].hasVoted = false
    conversationMessages.value[index].voteType = 0
    showAdviceInput.value[index] = false
  } else {
    // 选中状态：更新UI
    conversationMessages.value[index].hasVoted = true
    conversationMessages.value[index].voteType = vote
    // 选中“一般”时显示建议输入框，选中“有用”时隐藏
    showAdviceInput.value[index] = vote === 2
    if (vote === 2) {
      adviceInputs.value[index] = '' // 重置建议输入框
      scrollToBottom()
    }
  }
  conversationMessages.value = [...conversationMessages.value]

  try {
    // 获取用户ID或客户群ID
    const userId = localStorage.getItem('wecom_external_userid')
    if (!userId) throw new Error('未找到用户ID')

    // 获取当前消息
    const message = conversationMessages.value[index]
    // 获取当前选中的style，优先使用default_style，否则使用currentVersion或默认值1
    const currentStyle = getSelectedStyle(message, index)

    // 尝试从message.style中获取对应style的record_id
    let record_id = message.record_id
    if (message.style && Array.isArray(message.style)) {
      const styleContent = message.style.find((item) => Number(item.style) === Number(currentStyle))
      if (styleContent && styleContent.record_id) {
        record_id = styleContent.record_id
      }
    }

    if (!record_id) {
      // 从IndexedDB获取所有聊天记录
      const historyRecords = await indexedDB.getUserChatRecords(userId)
      const recordIndex = Math.floor(index / 2)
      if (
        !historyRecords ||
        historyRecords.length <= recordIndex ||
        !historyRecords[recordIndex].record_id
      ) {
        throw new Error('未找到对应的聊天记录')
      }
      record_id = historyRecords[recordIndex].record_id
    }

    // 2. 调用接口：取消操作(vote=0)和有用(vote=1)时直接提交
    if (vote === 0 || vote === 1) {
      await sendMessageVote(userId, record_id, vote, null)
      // 获取完整的聊天记录
      const historyRecords = await indexedDB.getUserChatRecords(userId)
      const recordIndex = Math.floor(index / 2)
      const record = historyRecords[recordIndex]

      // 更新对应style的投票状态
      if (record && record.style && Array.isArray(record.style)) {
        record.style = record.style.map((styleItem) => {
          if (Number(styleItem.style) === Number(currentStyle)) {
            return {
              ...styleItem,
              vote: vote,
              advice: null,
              hasVoted: vote !== 0, // 只有非取消状态才标记为已投票
              voteType: vote,
            }
          }
          return styleItem
        })
        console.log('更新后的record:', record)
        // 更新到IndexedDB
        await indexedDB.updateChatRecordByRecordId(record.record_id, {
          style: record.style,
        })
      }
      // 同时更新内存中message.style数组的投票状态
      if (
        conversationMessages.value[index].style &&
        Array.isArray(conversationMessages.value[index].style)
      ) {
        conversationMessages.value[index].style = conversationMessages.value[index].style.map(
          (styleItem) => {
            if (Number(styleItem.style) === Number(currentStyle)) {
              return {
                ...styleItem,
                vote: vote,
                advice: null,
                hasVoted: vote !== 0,
                voteType: vote,
              }
            }
            return styleItem
          },
        )
        conversationMessages.value = [...conversationMessages.value]
      }
      // 显示对应提示
      showToast(vote === 0 ? '已取消选择' : '感谢您的肯定！')
      showAdviceInput.value[index] = false
      if (vote === 1) {
        showAdviceInput.value[index] = false // 确保有用时隐藏建议框
      }
    }
  } catch (error) {
    console.error('投票操作失败:', error)
    showToast(`操作失败: ${error.message}`)
    // 失败时回滚UI状态
    if (vote === 0) {
      conversationMessages.value[index].hasVoted = true
      conversationMessages.value[index].voteType = conversationMessages.value[index].voteType || 1
    } else {
      conversationMessages.value[index].hasVoted = false
      conversationMessages.value[index].voteType = 0
    }
    conversationMessages.value = [...conversationMessages.value]
  }
}

// 提交投票
const submitVote = async (index, userId, record_id, vote, advice) => {
  try {
    // 获取当前消息
    const message = conversationMessages.value[index]
    // 获取当前选中的style，优先使用default_style，否则使用currentVersion或默认值1
    const currentStyle = getSelectedStyle(message, index)
    // 调用sendMessageVote方法发送投票信息
    await sendMessageVote(userId, record_id, vote, advice)

    // 标记消息已投票和投票类型
    conversationMessages.value[index].hasVoted = true
    conversationMessages.value[index].voteType = vote
    conversationMessages.value[index].advice = advice
    // 隐藏建议输入框
    showAdviceInput.value[index] = false
    showToast(vote === 1 ? '感谢您的肯定！' : '感谢您的反馈！')

    // 获取完整的聊天记录
    const historyRecords = await indexedDB.getUserChatRecords(userId)
    const recordIndex = Math.floor(index / 2)
    const record = historyRecords[recordIndex]

    // 更新对应style的投票状态
    if (record && record.style && Array.isArray(record.style)) {
      record.style = record.style.map((styleItem) => {
        if (Number(styleItem.style) === Number(currentStyle)) {
          return {
            ...styleItem,
            vote: vote,
            advice: advice,
            hasVoted: true,
            voteType: vote,
          }
        }
        return styleItem
      })

      // 更新到IndexedDB
      await indexedDB.updateChatRecordByRecordId(record.record_id, {
        style: record.style,
      })
      // 同时更新内存中message.style数组的投票状态
      if (
        conversationMessages.value[index].style &&
        Array.isArray(conversationMessages.value[index].style)
      ) {
        conversationMessages.value[index].style = conversationMessages.value[index].style.map(
          (styleItem) => {
            if (Number(styleItem.style) === Number(currentStyle)) {
              return {
                ...styleItem,
                vote: vote,
                advice: advice,
                hasVoted: true,
                voteType: vote,
              }
            }
            return styleItem
          },
        )
        conversationMessages.value = [...conversationMessages.value]
      }
    }
  } catch (error) {
    console.error('提交投票失败:', error)
  }
}

// 处理建议提交
const handleSubmitAdvice = async (index) => {
  try {
    // 获取建议内容
    const advice = adviceInputs.value[index]?.trim()
    if (!advice) {
      showToast('请输入建议内容')
      return
    }

    // 获取userId
    const userId = localStorage.getItem('wecom_external_userid')
    if (!userId) {
      throw new Error('未找到用户ID')
    }

    // 获取当前消息
    const message = conversationMessages.value[index]
    // 获取当前选中的style，优先使用default_style，否则使用currentVersion或默认值1
    const currentStyle = getSelectedStyle(message, index)

    // 尝试从message.style中获取对应style的record_id
    let record_id = message.record_id
    if (message.style && Array.isArray(message.style)) {
      const styleContent = message.style.find((item) => Number(item.style) === Number(currentStyle))
      if (styleContent && styleContent.record_id) {
        record_id = styleContent.record_id
      }
    }

    if (!record_id) {
      // 获取对应的record_id
      const historyRecords = await indexedDB.getUserChatRecords(userId)
      const recordIndex = Math.floor(index / 2)

      if (
        !historyRecords ||
        historyRecords.length <= recordIndex ||
        !historyRecords[recordIndex].record_id
      ) {
        throw new Error('未找到对应的聊天记录或record_id')
      }

      record_id = historyRecords[recordIndex].record_id
    }

    // 提交投票（一般评价，vote=2）
    await submitVote(index, userId, record_id, 2, advice)
    showAdviceInput.value[index] = false
  } catch (error) {
    console.error('提交建议失败:', error)
    showToast(`操作失败: ${error.message || '未知错误'}`)
  }
}

// 取消建议输入
const handleCancelAdvice = (index) => {
  showAdviceInput.value[index] = false
  adviceInputs.value[index] = ''
}

const escapeHtml = (text) => {
  return String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

const extractVideoItems = (raw) => {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    const items = Array.isArray(parsed) ? parsed : [parsed]
    return items
      .filter((item) => item?.msgtype === 'video' && item?.video?.url)
      .map((item) => ({
        url: item.video.url,
        title: item.video.title || '视频内容',
        description: item.video.description || '',
      }))
  } catch (error) {
    return []
  }
}

const extractVideoItemsFromContent = (rawContent) => {
  if (!rawContent) return []
  let text = String(rawContent)
  const collectedVideoItems = []
  const appendVideoItems = (items) => {
    items.forEach((item) => {
      if (!collectedVideoItems.some((x) => x.url === item.url)) {
        collectedVideoItems.push(item)
      }
    })
  }

  if (
    /^"\s*[\s\S]*\s*"$/.test(text) &&
    (text.includes('\\"') || text.includes('\\n') || text.includes('```json'))
  ) {
    try {
      text = JSON.parse(text)
    } catch (error) {
      // 忽略，继续按原始文本处理
    }
  }

  const jsonCodeBlockRegex = /```json\s*([\s\S]*?)\s*```/gi
  text.replace(jsonCodeBlockRegex, (match, jsonText) => {
    appendVideoItems(extractVideoItems(jsonText))
    return ''
  })

  if (collectedVideoItems.length === 0 && text.includes('"msgtype"') && text.includes('"video"')) {
    const fallbackJsonMatch = text.match(/\[\s*\{[\s\S]*?"msgtype"\s*:\s*"video"[\s\S]*?\}\s*\]/i)
    if (fallbackJsonMatch) {
      appendVideoItems(extractVideoItems(fallbackJsonMatch[0]))
    }
  }

  return collectedVideoItems
}

const markdownToPlainText = (markdownText) => {
  if (!markdownText) return ''
  const parsedHtml = parseMarkdown(markdownText)
  return parsedHtml
    .replace(/<a[^>]*href="([^"]+)"[^>]*>([^<]+)<\/a>/g, (match, href, text) => {
      return `【${text}】→${href}`
    })
    .replace(/<strong>([^<]+)<\/strong>/g, '$1')
    .replace(/<\/p>/g, '\n')
    .replace(/<li[^>]*>/g, '\n  - ')
    .replace(/<\/li>/g, '')
    .replace(/<br\s*\/?>/g, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/\n{2,}/g, '\n')
    .replace(/^\s+|\s+$/g, '')
    .replace(/\n\s{3,}-/g, '\n  - ')
    .trim()
}

/** 服务端返回的带 ?size=min / &size=min 的预览图、以及 .mp4 直链，不在正文与气泡中展示 */
const isMarkdownImageWithSizeQuery = (imageUrl) => {
  const s = String(imageUrl).trim()
  if (/[?&]size=min/i.test(s)) return true
  const pathOnly = s.split(/[#?]/)[0]
  return /\.mp4$/i.test(pathOnly)
}

const stripMediaFromContent = (rawContent) => {
  if (!rawContent) {
    return { text: '', mediaSuffix: '' }
  }

  let content = String(rawContent)
  if (
    /^"\s*[\s\S]*\s*"$/.test(content) &&
    (content.includes('\\"') || content.includes('\\n') || content.includes('```json'))
  ) {
    try {
      content = JSON.parse(content)
    } catch (error) {
      // ignore parse error and keep original text
    }
  }

  const mediaParts = []
  let textOnly = content

  const imageRegex = /!\[(.*?)\]\((\s*https?:\/\/[^)]+\s*)(\s+".*?")?\)/gi
  textOnly = textOnly.replace(imageRegex, (match, _alt, urlPart) => {
    if (isMarkdownImageWithSizeQuery(urlPart)) {
      return ''
    }
    mediaParts.push(match.trim())
    return ''
  })

  const videoBlockRegex = /```json\s*([\s\S]*?)\s*```/gi
  textOnly = textOnly.replace(videoBlockRegex, (match, jsonText) => {
    const videoItems = extractVideoItems(jsonText)
    if (videoItems.length > 0) {
      mediaParts.push(`\`\`\`json\n${jsonText.trim()}\n\`\`\``)
      return ''
    }
    return match
  })

  if (mediaParts.length === 0 && textOnly.includes('"msgtype"') && textOnly.includes('"video"')) {
    const fallbackJsonMatch = textOnly.match(/\[\s*\{[\s\S]*?"msgtype"\s*:\s*"video"[\s\S]*?\}\s*\]/i)
    if (fallbackJsonMatch) {
      mediaParts.push(`\`\`\`json\n${fallbackJsonMatch[0].trim()}\n\`\`\``)
      textOnly = textOnly.replace(fallbackJsonMatch[0], '')
    }
  }

  const normalizedText = textOnly.replace(/\n{3,}/g, '\n\n').replace(/^\s+|\s+$/g, '')

  return {
    text: normalizedText,
    mediaSuffix: mediaParts.join('\n\n'),
  }
}

const buildOrderedMessageSegments = (rawContent) => {
  if (!rawContent) return []
  let text = String(rawContent)

  if (
    /^"\s*[\s\S]*\s*"$/.test(text) &&
    (text.includes('\\"') || text.includes('\\n') || text.includes('```json'))
  ) {
    try {
      text = JSON.parse(text)
    } catch (error) {
      // 保持原样继续解析
    }
  }

  const segments = []
  const tokenRegex =
    /!\[(.*?)\]\((\s*https?:\/\/[^)]+\s*)(\s+".*?")?\)|```json\s*([\s\S]*?)\s*```|(\[\s*\{[\s\S]*?"msgtype"\s*:\s*"video"[\s\S]*?\}\s*\])/gi
  let lastIndex = 0
  let match

  while ((match = tokenRegex.exec(text)) !== null) {
    const beforeText = text.slice(lastIndex, match.index)
    if (beforeText && beforeText.trim()) {
      segments.push({ type: 'text', content: beforeText })
    }

    if (match[2]) {
      const imgUrl = match[2].trim()
      if (!isMarkdownImageWithSizeQuery(imgUrl)) {
        segments.push({ type: 'image', url: imgUrl })
      }
    } else {
      const jsonText = match[4] || match[5]
      const videoItems = extractVideoItems(jsonText)
      videoItems.forEach((video) => {
        segments.push({ type: 'video', ...video })
      })
    }

    lastIndex = tokenRegex.lastIndex
  }

  const tailText = text.slice(lastIndex)
  if (tailText && tailText.trim()) {
    segments.push({ type: 'text', content: tailText })
  }

  return segments
}

// 设计Markdown解析器，移除Markdown分隔符并正确解析内容
const parseMarkdown = (content) => {
  if (!content) return ''

  try {
    // 确保内容是字符串
    let html = String(content)
    let videoHtml = ''
    const collectedVideoItems = []
    const appendVideoItems = (items) => {
      items.forEach((item) => {
        if (!collectedVideoItems.some((x) => x.url === item.url)) {
          collectedVideoItems.push(item)
        }
      })
    }

    // 兼容 indexedDB 中可能被序列化成字符串字面量（含 \" 和 \n）的场景
    if (
      /^"\s*[\s\S]*\s*"$/.test(html) &&
      (html.includes('\\"') || html.includes('\\n') || html.includes('```json'))
    ) {
      try {
        html = JSON.parse(html)
      } catch (error) {
        // 保持原样，继续后续解析
      }
    }

    // 解析任意位置的 ```json ... ``` 代码块，提取视频并从正文移除
    const jsonCodeBlockRegex = /```json\s*([\s\S]*?)\s*```/gi
    html = html.replace(jsonCodeBlockRegex, (match, jsonText) => {
      appendVideoItems(extractVideoItems(jsonText))
      return ''
    })

    // 兜底：若没有标准代码块，尝试从全文中提取含 msgtype=video 的 JSON 数组
    if (collectedVideoItems.length === 0 && html.includes('"msgtype"') && html.includes('"video"')) {
      const fallbackJsonMatch = html.match(/\[\s*\{[\s\S]*?"msgtype"\s*:\s*"video"[\s\S]*?\}\s*\]/i)
      if (fallbackJsonMatch) {
        appendVideoItems(extractVideoItems(fallbackJsonMatch[0]))
        html = html.replace(fallbackJsonMatch[0], '')
      }
    }

    if (collectedVideoItems.length > 0) {
      videoHtml = collectedVideoItems
        .map(
          (video) => `
          <div class="embedded-video-card">
            <video class="embedded-video-player" controls playsinline preload="metadata" referrerpolicy="no-referrer" src="${String(video.url).replace(/#.*$/, '')}#t=0.1"></video>
          </div>`,
        )
        .join('')
    }

    // 重要：移除Markdown分隔符（兼容 ```json / ``` json / ```markdown / 孤立```）
    // 移除开头的代码块类型标记
    html = html.replace(/^```\s*(markdown|json)\s*\n?/i, '')
    // 移除任意位置残留的 fence 行（```json、``` json、```）
    html = html.replace(/^\s*```\s*json\s*$/gim, '')
    html = html.replace(/^\s*```\s*$/gm, '')
    // 兜底：移除结尾残留```标记
    html = html.replace(/```\s*$/i, '')

    // 解析图片：![]() 格式
    // 带查询参数 size= 的预览图、.mp4 直链不输出（如 lke.cloud.tencent.com/... ?size=minl...）
    html = html.replace(
      /!\[(.*?)\]\((\s*https?:\/\/[^)]+\s*)(\s+".*?")?\)/g,
      (match, alt, url, title) => {
        // 去除URL前后空格
        const cleanUrl = url.trim()
        if (isMarkdownImageWithSizeQuery(cleanUrl)) {
          return ''
        }
        // 提取图片尺寸（如果有）
        let width = '100%'
        let height = 'auto'

        if (cleanUrl.includes('?size=min')) {
          const sizeMatch = cleanUrl.match(/size=min([^|]*)\|?([\d.]*\*[\d.]*)/)
          if (sizeMatch && sizeMatch[2]) {
            const [w, h] = sizeMatch[2].split('*')
            if (w && h) {
              const maxWidth = 300
              const imgWidth = parseFloat(w)
              width = imgWidth > maxWidth ? `${maxWidth}px` : `${imgWidth}px`
            }
          }
        }

        return `<img src="${cleanUrl}" alt="${alt || '图片'}" referrerpolicy="no-referrer" style="width: 100%; height:${height}; margin: 8px 0;" />`
      },
    )

    // 新增：解析文字链接 [链接文字](链接地址) 格式
    // 匹配 [文本](url)，支持可选的title属性（"标题"部分）
    // 排除已被图片解析匹配的![]()格式（通过负向前瞻确保前面不是!）
    html = html.replace(
      /(?<!\!)\[(.*?)\]\((\s*https?:\/\/[^)]+\s*)(\s+".*?")?\)/g,
      (match, linkText, url, title) => {
        const cleanUrl = url.trim() // 去除链接地址前后空格
        if (isMarkdownImageWithSizeQuery(cleanUrl)) {
          return ''
        }
        const cleanTitle = title ? title.trim().replace(/^"|"$/g, '') : linkText // 处理标题（去除引号）
        // 添加基础样式：蓝色、下划线、hover效果，保持与默认链接一致的用户体验
        return `<a href="${cleanUrl}" title="${cleanTitle}" style="color: #1890ff; text-decoration: underline; cursor: pointer;" target="_blank" rel="noopener noreferrer">${linkText}</a>`
      },
    )

    // 匹配 1-6个# + 可选空格 + 标题文本（行首开始）
    html = html.replace(/^(#{1,6})\s+(.*?)$/gm, (match, hashes, text) => {
      const level = hashes.length // #的数量 = 标题级别（1-6）
      return `<h${level} style="margin: 12px 0; font-size: ${24 - (level - 1) * 2}px; font-weight: 600;">${text}</h${level}>`
    })

    // 优化加粗格式解析
    // 处理标准加粗格式 **text**
    html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')

    // 处理可能包含空格的加粗格式 ** text **
    html = html.replace(/\*\*\s+([^*]+?)\s+\*\*/g, '<strong>$1</strong>')

    // 处理单个星号可能的转义情况
    html = html.replace(/\\\*\\\*([^*]+?)\\\*\\\*/g, '<strong>$1</strong>')

    // 处理换行符，确保文本正确换行
    html = html.replace(/\n/g, '<br>')

    // 处理列表项（简单实现）
    html = html.replace(/^- (.*?)$/gm, '<li style="margin: 4px 0;">$1</li>\n') // 新增\n强制换行
    html = html.replace(
      /(<li>[\s\S]*?<\/li>\n)/g, // 匹配带换行的li标签
      '<ul style="margin: 8px 0; padding-left: 20px;">$1</ul>',
    )

    // 确保列表嵌套正确
    html = html.replace(/<\/ul>\s*<ul>/g, '')

    // 处理段落间距
    html = html.replace(/<br>\s*<br>/g, '</p><p>')
    const hasBlockLikeTag = /<(p|h[1-6]|img|ul|ol|li|div|video)\b/i.test(html)
    if (!hasBlockLikeTag) {
      html = `<p style="margin: 8px 0; line-height: 1.6;">${html}</p>`
    }

    return `${html}${videoHtml}`
  } catch (error) {
    console.error('Markdown解析错误:', error)
    return content
  }
}

// 处理编辑按钮点击
const handleEditClick = (message, index) => {
  // 获取当前选中的style，优先使用default_style，否则使用currentVersion或默认值1
  const currentStyle = getSelectedStyle(message, index)
  currentEditingMessage.value = { ...message, index, style: currentStyle }
  const currentContent = getCurrentVersionContent(message, index) || message.content || ''
  const { text, mediaSuffix } = stripMediaFromContent(currentContent)
  editingContent.value = text
  currentEditingMediaSuffix.value = mediaSuffix
  showEditDialog.value = true
}

// 处理取消编辑
const handleCancelEdit = () => {
  showEditDialog.value = false
  editingContent.value = ''
  currentEditingMediaSuffix.value = ''
  currentEditingMessage.value = {}
}

// 处理保存编辑
const handleSaveEdit = async () => {
  try {
    // 获取userId
    const userId = localStorage.getItem('wecom_external_userid')
    if (!userId) {
      throw new Error('未找到用户ID')
    }

    // 获取当前消息
    const message = conversationMessages.value[currentEditingMessage.value.index]
    // 获取当前选中的style，优先使用default_style，否则使用currentVersion或默认值1
    const currentStyle = getSelectedStyle(message, currentEditingMessage.value.index)

    // 尝试从message.style中获取对应style的record_id
    let record_id = message.record_id
    if (message.style && Array.isArray(message.style)) {
      const styleContent = message.style.find((item) => Number(item.style) === Number(currentStyle))
      if (styleContent && styleContent.record_id) {
        record_id = styleContent.record_id
      }
    }

    if (!record_id) {
      // 获取对应的record_id
      const historyRecords = await indexedDB.getUserChatRecords(userId)
      // 根据消息类型确定记录索引
      let recordIndex
      if (message.type === 'user') {
        // 用户消息：索引为当前索引除以2
        recordIndex = Math.floor(currentEditingMessage.value.index / 2)
      } else {
        // AI消息：索引为(当前索引-1)除以2
        recordIndex = Math.floor((currentEditingMessage.value.index - 1) / 2)
      }

      if (
        !historyRecords ||
        historyRecords.length <= recordIndex ||
        !historyRecords[recordIndex].record_id
      ) {
        throw new Error(`未找到对应的聊天记录或record_id，索引: ${recordIndex}，记录数: ${historyRecords?.length || 0}`)
      }
      record_id = historyRecords[recordIndex].record_id
    }
    const updatedContent = editingContent.value.trim()

    if (!updatedContent) {
      showToast('编辑内容不能为空')
      return
    }

    // const mergedContent = currentEditingMediaSuffix.value
    //   ? `${updatedContent}\n\n${currentEditingMediaSuffix.value}`
    //   : updatedContent

    const mergedContent = currentEditingMediaSuffix.value
      ? `${currentEditingMediaSuffix.value}\n\n${updatedContent}`
      : updatedContent

    // 调用编辑接口
    await editMessage(userId, record_id, mergedContent)

    // 更新IndexedDB中的记录
    const historyRecords = await indexedDB.getUserChatRecords(userId)
    const recordIndex = Math.floor(currentEditingMessage.value.index / 2)
    const record = historyRecords[recordIndex]

    if (record && record.style && Array.isArray(record.style)) {
      // 更新style数组中的对应项
      record.style = record.style.map((styleItem) => {
        if (Number(styleItem.style) === Number(currentStyle)) {
          return {
            ...styleItem,
            content: mergedContent,
            vote: 0,
            advice: null,
            hasVoted: false,
            voteType: 0,
          }
        }
        return styleItem
      })

      // 更新到IndexedDB
      await indexedDB.updateChatRecordByRecordId(record_id, {
        style: record.style,
        vote: 0,
        advice: null,
      })
    } else {
      // 如果没有style数组，则直接更新消息内容
      await indexedDB.updateChatRecordByRecordId(record_id, {
        content: mergedContent,
        vote: 0,
        advice: null,
      })
    }

    // 更新组件内的消息内容
    const messageIndex = currentEditingMessage.value.index

    if (
      conversationMessages.value[messageIndex].style &&
      Array.isArray(conversationMessages.value[messageIndex].style)
    ) {
      // 更新style数组中的对应项
      conversationMessages.value[messageIndex].style = conversationMessages.value[
        messageIndex
      ].style.map((styleItem) => {
        if (Number(styleItem.style) === Number(currentStyle)) {
          return {
            ...styleItem,
            content: mergedContent,
            vote: 0,
            advice: null,
            hasVoted: false,
            voteType: 0,
          }
        }
        return styleItem
      })
      // 同时更新消息本身的投票状态
      conversationMessages.value[messageIndex].voteType = 0
      conversationMessages.value[messageIndex].advice = null
      conversationMessages.value[messageIndex].hasVoted = false
    } else {
      // 如果没有style数组，则直接更新消息内容
      conversationMessages.value[messageIndex].content = mergedContent
      conversationMessages.value[messageIndex].voteType = 0
      conversationMessages.value[messageIndex].advice = null
      conversationMessages.value[messageIndex].hasVoted = false
    }

    // 确保触发响应式更新
    conversationMessages.value = [...conversationMessages.value]

    // 隐藏建议输入框（如果有的话）
    showAdviceInput.value[currentEditingMessage.value.index] = false

    showToast('编辑成功')

    // 关闭弹出框
    showEditDialog.value = false
    editingContent.value = ''
    currentEditingMediaSuffix.value = ''
    currentEditingMessage.value = {}
  } catch (error) {
    console.error('保存编辑失败:', error)
    showToast(`保存失败: ${error.message || '未知错误'}`)
  }
}

// 发送当前回复到企业微信（包含图片处理）
const handleSend = async (content, index) => {
  try {
    console.log('[handleSend] start', { index })
    // 获取userId
    const userId = localStorage.getItem('wecom_external_userid')
    if (!userId) {
      throw new Error('未找到用户ID')
    }

    // 获取对应的record_id
    // 获取当前消息
    const message = conversationMessages.value[index]
    // 获取当前选中的style，优先使用default_style，否则使用currentVersion或默认值1
    const currentStyle = getSelectedStyle(message, index)

    // 尝试从message.style中获取对应style的record_id
    let record_id = message.record_id
    if (message.style && Array.isArray(message.style)) {
      const styleContent = message.style.find((item) => Number(item.style) === Number(currentStyle))
      if (styleContent && styleContent.record_id) {
        record_id = styleContent.record_id
      }
    }

    if (!record_id) {
      // 获取对应的record_id
      const historyRecords = await indexedDB.getUserChatRecords(userId)
      const recordIndex = Math.floor(index / 2)
      if (
        !historyRecords ||
        historyRecords.length <= recordIndex ||
        !historyRecords[recordIndex].record_id
      ) {
        throw new Error('未找到对应的聊天记录或record_id')
      }
      record_id = historyRecords[recordIndex].record_id
    }
    // getCurrentVersionContent获取当前显示的内容，确保获取到正确的媒体URL
    const currentContent = getCurrentVersionContent(message, index)
    console.log('[handleSend] currentContent length', currentContent?.length || 0)
    // 1. 按原文顺序构建片段（text/image/video）
    const orderedSegments = buildOrderedMessageSegments(currentContent)
    console.log('[handleSend] orderedSegments', orderedSegments)

    // 2. 获取图片/视频对应的media_id
    const mediaUrls = [
      ...new Set(
        orderedSegments
          .filter((segment) => segment.type === 'image' || segment.type === 'video')
          .map((segment) => segment.url),
      ),
    ]
    const mediaIdMap = {}
    if (mediaUrls.length > 0) {
      try {
        const mediaResponse = await getImageMediaIds(mediaUrls)
        console.log('[handleSend] mediaResponse', mediaResponse)
        if (typeof mediaResponse === 'object' && mediaResponse !== null) {
          // 检查是否有 data 字段（API 标准返回格式）
          const mediaMap = mediaResponse.data || mediaResponse
          mediaUrls.forEach((url) => {
            const mediaId = mediaMap[url] || mediaMap[`${url}:`]
            if (mediaId) {
              mediaIdMap[url] = mediaId
            } else {
              console.warn(`未找到媒体URL ${url} 对应的media_id`)
            }
          })
        }
      } catch (mediaError) {
        console.warn('获取媒体media_id失败，将仅发送文本内容:', mediaError)
        showToast('媒体处理失败，将仅发送文字内容')
      }
    }

    // 3. 构造企业微信消息（严格按原文顺序）
    const messages = []
    orderedSegments.forEach((segment) => {
      if (segment.type === 'text') {
        const textContent = markdownToPlainText(segment.content)
        if (textContent) {
          messages.push({
            msgtype: 'text',
            text: {
              content: textContent,
            },
          })
        }
        return
      }

      if (segment.type === 'image') {
        const mediaId = mediaIdMap[segment.url]
        if (mediaId) {
          messages.push({
            msgtype: 'image',
            image: {
              mediaid: mediaId,
            },
          })
        }
        return
      }

      if (segment.type === 'video') {
        const mediaId = mediaIdMap[segment.url]
        if (mediaId) {
          messages.push({
            msgtype: 'video',
            video: {
              mediaid: mediaId,
              title: segment.title || '视频内容',
              description: segment.description || '',
            },
          })
        } else {
          // 视频未成功换取media_id时，降级发送视频链接，避免内容丢失
          messages.push({
            msgtype: 'text',
            text: {
              content: segment.url,
            },
          })
        }
      }
    })

    // 4. 发送所有消息
    if (messages.length === 0) {
      throw new Error('没有可发送的内容')
    }
    console.log('[handleSend] messages to send', messages)

    // 依次发送每条消息
    for (const msg of messages) {
      // 直接传递正确格式的消息体，不添加额外参数
      await wecomSendChatMessage(msg, userId)
      // 仅记录一次发送状态（避免重复记录）
      if (messages.indexOf(msg) === 0) {
        await sendMessageRecord(userId, record_id)
      }
      // 间隔300ms发送下一条，避免企业微信接口限流
      await new Promise((resolve) => setTimeout(resolve, 300))
    }

    showToast('发送成功')
  } catch (error) {
    console.error('[handleSend] 发送失败:', error)
    showToast(`发送失败: ${error?.message || '未知错误'}`)
  }
}

onMounted(async () => {
  // 获取全局默认风格
  try {
    const defaultStyleData = await getDefalutStyle()
    globalDefaultStyle.value = defaultStyleData?.default_style || null
    console.log('全局默认风格:', globalDefaultStyle.value)
  } catch (error) {
    globalDefaultStyle.value = null
  }
  // 组件挂载时初始化showAdviceInput状态，添加调试日志
  if (props.initialMessages && Array.isArray(props.initialMessages)) {
    // 从IndexedDB获取记录，添加default_style字段
    const userId = localStorage.getItem('wecom_external_userid')
    let historyRecords = []
    if (userId) {
      try {
        historyRecords = await indexedDB.getUserChatRecords(userId)
      } catch (error) {
        console.error('从IndexedDB获取记录失败:', error)
      }
    }
    props.initialMessages.forEach((message, index) => {
      if (message.type === 'assistant' && message.advice) {
        showAdviceInput.value[index] = false
        adviceInputs.value[index] = message.advice
      }
      // 初始化版本切换状态，消息带default_style时直接同步
      if (message.default_style !== null && message.default_style !== undefined) {
        currentVersion.value[index] = normalizeStyle(message.default_style)
      } else if (currentVersion.value[index] === undefined) {
        // 查找对应的记录
        const recordIndex = Math.floor(index / 2) // 假设每两条消息（用户+AI）对应一条记录
        if (historyRecords.length > recordIndex) {
          const record = historyRecords[recordIndex]
          if (record && record.default_style) {
            currentVersion.value[index] = normalizeStyle(record.default_style)
            // 更新消息对象，添加default_style字段
            conversationMessages.value[index] = {
              ...conversationMessages.value[index],
              default_style: normalizeStyle(record.default_style),
            }
          } else {
            currentVersion.value[index] = normalizeStyle(globalDefaultStyle.value ?? 1)
          }
        } else {
          currentVersion.value[index] = normalizeStyle(globalDefaultStyle.value ?? 1)
        }
      }
    })
  }
  conversationMessages.value.forEach((message, index) => {
    if (message.type === 'assistant' && message.advice) {
      showAdviceInput.value[index] = false
      adviceInputs.value[index] = message.advice
    }
    // 初始化版本切换状态，默认选中详细版
    if (currentVersion.value[index] === undefined) {
      currentVersion.value[index] = getSelectedStyle(message, index)
    }
  })
})

// 组件卸载前清除定时器
onBeforeUnmount(() => {
  if (loadingInterval) {
    clearInterval(loadingInterval)
  }
})
</script>

<template>
  <div class="chat-container">
    <NewsControl
      v-if="globalDefaultStyle === null && !isAnyMessageGenerating"
      :globalDefaultStyle="globalDefaultStyle"
      @tooltip-shown="
        () => {
          scrollToLatestAssistantMessage()
          isTooltipVisible = true
        }
      "
      @tooltip-closed="
        () => {
          scrollToBottom()
          isTooltipVisible = false
        }
      "
    />
    <div class="conversation-content">
      <!-- 欢迎消息 -->
      <div class="aIfirst-message">
        <img src="@/assets/AiImage.png" alt="智能助手" class="message-avatar" />
        <div class="message-card">
          <div class="card-content">
            您好～我是<span style="font-weight: 600">金领冠智能话术顾问。</span>
            工作上有不会答的问题、活动细节记不清、系统操作卡壳，都可以来问我。
            我随时在，帮您更快更专业地服务每一位妈妈。
          </div>
        </div>
      </div>

      <!-- 对话列表 -->
      <div
        v-for="(message, index) in conversationMessages"
        :key="index"
        :class="['message', message.type]"
      >
        <!-- 用户消息 -->
        <div v-if="message.type === 'user'" class="user-message">
          <div class="message-bubble user" v-html="parseMarkdown(message.content || '')"></div>
        </div>

        <!-- 助手消息 -->
        <div v-else class="assistant-message">
          <img src="@/assets/ai.png" alt="智能助手" class="message-avatar" />
          <div class="message-card">
            <div class="card-header">
              <div class="version-switcher">
                <!-- <div class="external-tooltip" v-if="isTooltipVisible">
                  <div class="tooltip-arrow"></div>
                </div> -->
                <div class="tooltipBtn" v-if="isTooltipVisible">
                  <button
                    class="version-btn"
                    :class="{ active: getSelectedStyle(message, index) === 1 }"
                    @click="switchVersion(index, 1)"
                  >
                    详细版
                  </button>
                  <button
                    class="version-btn"
                    style="margin-right:5px"
                    :class="{ active: getSelectedStyle(message, index) === 2 }"
                    @click="switchVersion(index, 2)"
                  >
                    简化版
                  </button>
                </div>
                <div class="tooltipBtnfal" v-else>
                  <button
                    class="version-btn"
                    :class="{ active: getSelectedStyle(message, index) === 1 }"
                    @click="switchVersion(index, 1)"
                  >
                    详细版
                  </button>
                  <button
                    class="version-btn"
                    style="margin-right:5px;"
                    :class="{ active: getSelectedStyle(message, index) === 2 }"
                    @click="switchVersion(index, 2)"
                  >
                    简化版
                  </button>
                </div>
              </div>
            </div>
            <div class="card-content">
              <!-- 确保每次都重新计算解析结果 -->
              <div v-html="parseMarkdown(getCurrentVersionContent(message, index) || '')"></div>
              <!-- 生成中状态：只在isGenerating为true且内容为空时显示加载提示 -->
              <div
                v-if="
                  isCurrentVersionGenerating(message, index) &&
                  (!getCurrentVersionContent(message, index) ||
                    !getCurrentVersionContent(message, index).trim())
                "
                class="loading-message"
              >
                {{ loadingMessages[currentLoadingMessage] }}
              </div>
            </div>
            <div class="card-actions">
              <div class="actions-left">
                <!-- 有用按钮 -->
                <button
                  class="action-btn"
                  title="有用"
                  @click="handleVote(index, message.hasVoted && message.voteType === 1 ? 0 : 1)"
                  :class="{ voted: message.hasVoted && message.voteType === 1 }"
                >
                  <img
                    v-if="message.hasVoted && message.voteType === 1"
                    src="@/assets/goodOn.png"
                    alt=""
                  />
                  <img v-else src="@/assets/good.png" alt="" />
                </button>

                <!-- 一般按钮 -->
                <button
                  class="action-btn"
                  title="一般"
                  @click="handleVote(index, message.hasVoted && message.voteType === 2 ? 0 : 2)"
                  :class="{ voted: message.hasVoted && message.voteType === 2 }"
                >
                  <img
                    v-if="message.hasVoted && message.voteType === 2"
                    src="@/assets/fairOn.png"
                    alt="已选一般"
                  />
                  <img v-else src="@/assets/fair.png" alt="一般" />
                </button>
                <button class="action-btn" title="重新生成" @click="handleRefresh(index)">
                  <img src="@/assets/refresh.png" alt="" />
                </button>
              </div>
              <div class="action-right">
                <button
                  class="edit-btn"
                  @click="handleEditClick(message, index)"
                  :disabled="message.isGenerating"
                >
                  编辑
                </button>
                <button
                  class="send-btn"
                  @click="handleSend(message.content, index)"
                  :disabled="message.isGenerating"
                >
                  发送
                </button>
              </div>
            </div>
            <!-- 建议输入框 - 放在card-actions外部，确保正确的布局 -->
            <div v-if="showAdviceInput[index]" class="advice-input-container">
              <div class="advice-input-wrapper">
                <textarea
                  v-model="adviceInputs[index]"
                  class="advice-textarea"
                  placeholder="建议再描述清楚一点
                  例如：
                  1、
                  2、"
                  rows="4"
                ></textarea>
                <button class="advice-send-btn" @click="handleSubmitAdvice(index)">
                  <img src="@/assets/send.png" alt="发送" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 编辑弹出框 -->
    <div v-if="showEditDialog" class="edit-dialog-overlay" @click.self="handleCancelEdit">
      <div class="edit-dialog">
        <div class="dialog-header">
          <img src="@/assets/ai-Frame.png" alt="" />
          <h3>话术建议</h3>
        </div>
        <div class="dialog-content">
          <textarea
            v-model="editingContent"
            class="edit-content-textarea"
            placeholder="请编辑话术内容"
            rows="10"
          ></textarea>
        </div>
        <div class="dialog-footer">
          <button class="cancel-btn" @click="handleCancelEdit">取消</button>
          <button class="save-btn" @click="handleSaveEdit">保存</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
body {
  overflow-x: hidden;
  width: 100%;
  margin-bottom: constant(safe-area-inset-bottom) !important;
  margin-bottom: env(safe-area-inset-bottom) !important;
}
</style>

<style scoped>
/* 仅保留对话内容相关样式 */
.conversation-content {
  flex: 1;
  overflow-y: visible; /* 禁用组件内部滚动，使用父容器的滚动 */
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background-color: #f4f5fc;
  min-height: 0; /* 确保flex子项正确收缩 */
}

/* 编辑弹出框样式 */
.edit-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #000000cc;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
  pointer-events: auto;
  overflow: hidden;
  bottom: calc(-1 * env(safe-area-inset-bottom));
  padding-bottom: env(safe-area-inset-bottom);
}

.edit-dialog {
  background: white;
  padding: 0.5rem 0.5rem 0 0.5rem;
  border-radius: 2px 12px 12px 12px;
  width: 80%;
  max-width: 400px;
  max-height: 60vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 10000;
}

.dialog-header {
  display: flex;
  flex-direction: row;
  padding: 0.2rem;
}

.dialog-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-left: 0.3rem;
}
.dialog-header img {
  width: 20px;
  height: 20px;
}

.dialog-content {
  padding: 0.2rem 0.5rem;
  flex: 1;
  overflow-y: auto;
  /* 隐藏滚动条但保留滚动功能 */
  -ms-overflow-style: none; /* IE 和 Edge */
  scrollbar-width: none; /* Firefox */
}

.dialog-content::-webkit-scrollbar {
  display: none; /* Chrome, Safari 和 Opera */
}

.edit-content-textarea {
  width: 100%;
  min-height: 200px;
  /* padding: 0.75rem; */
  border: 1px solid #fff;
  border-radius: 4px;
  font-size: 14px;
  line-height: 1.5;
  color: #333;
  white-space: pre-wrap;
  word-break: break-word;
  resize: vertical;
  box-sizing: border-box;
  font-family: inherit;
}

.edit-content-textarea:focus {
  outline: none;
  border-color: #4096ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

.dialog-footer {
  padding: 0.5rem 0.5rem;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.cancel-btn,
.save-btn {
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  background-color: #fff;
}

.cancel-btn {
  border: 1px solid #999999;
  color: #999999;
}

.cancel-btn:hover {
  /* border: 1px solid #e8e8e8; */
  /* background-color: #e8e8e8; */
}

.save-btn {
  border: 1px solid #3b6ce3;
  color: #3b6ce3;
}

.save-btn:hover {
  /* background-color: #4096ff; */
}

.aIfirst-message {
  display: flex;
  flex-direction: column;
  /* gap: 0.5rem; */
}
.aIfirst-message img {
  width: 102px;
  height: 200px;
  object-fit: cover;
  margin-top: 0;
  margin-left: 0.3rem;
  z-index: 10;
}
.aIfirst-message .message-card {
  background-color: #fff;
  border: 1px solid #3b6ce3;
  border-radius: 12px 2px 12px 12px;
  overflow: hidden;
  width: 75%;
  padding: 0.5rem;
  margin-top: -4.5rem;
  margin-left: 0.75rem;
  box-shadow: 0 2px 8px rgba(59, 108, 227, 0.1);
  z-index: 11;
}
.aIfirst-message .card-content {
  padding: 0.75rem;
  color: #333;
  line-height: 1.5;
  font-size: 14px;
}

.message {
  display: flex;
  align-items: flex-start;
}

/* 用户消息 */
.user-message {
  display: flex;
  justify-content: flex-end; /* 确保内容靠右对齐 */
  width: 100%; /* 占满父容器宽度，为靠右对齐提供基础 */
  box-sizing: border-box;
}

/* 用户消息气泡 */
.user-message .message-bubble {
  /* 移除重复的display属性，统一使用inline-block */
  display: inline-block;
  background-color: #3b6ee3;
  color: white;
  border-radius: 12px 0 12px 12px;
  padding: 0.5rem 0.5rem;
  max-width: 77%; /* 与AI消息宽度保持一致 */
  word-wrap: break-word;
  line-height: 1.5;
}

/* 助手消息 */
.assistant-message {
  display: flex;
  align-items: flex-start;
  /* gap: 0.5rem; */
  /* padding-left: 0.75rem; */
}
.message-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  margin-top: -1.3rem;
  margin-right: -1.5rem;
  z-index: 30;
}
.message-card {
  background-color: white;
  border: 1px solid #3b6ce3;
  border-radius: 2px 12px 12px 12px;
  overflow: hidden;
  width: 77%;
  min-width: 280px;
  padding-bottom: 8px;
  will-change: contents;
  word-wrap: break-word; /* 允许长单词换行 */
  word-break: break-all; /* 强制在容器边界换行（包括URL） */
  overflow-wrap: break-word; /* 兼容更多浏览器 */
  box-sizing: border-box; /* 确保padding不增加总宽度 */
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 100%;
  padding: 0.5rem 0.75rem;
}
.version-switcher {
  display: flex;
  /* align-items: center; */
  gap: 0.25rem;
  position: relative;
}

.tooltipBtn {
  z-index: 1000; /* 确保在遮罩层之上 */
  border: 1px solid #333;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-radius: 20px;
  padding:4px;
  background:#ccc;
  display: flex;
  gap: 0.25rem;
}
.tooltipBtnfal{
  display: flex;
  gap: 0.25rem;
}
.version-btn {
  padding: 0.25rem 0.75rem;
  border: 1px solid #3b6ce3;
  border-radius: 12px;
  background-color: white;
  color: #3b6ce3;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.version-btn.active {
  background-color: #3b6ce3;
  color: white;
}
.card-content {
  padding: 0 0.75rem 0.75rem;
  color: #333;
  line-height: 1.5;
  word-wrap: break-word;
  transition: all 0.1s ease-out;
  /* 新增：强制重绘 */
  will-change: contents;
  word-wrap: break-word; /* 允许长单词换行 */
  word-break: break-all; /* 强制在容器边界换行（包括URL） */
  overflow-wrap: break-word; /* 兼容更多浏览器 */
  max-width: 100%; /* 限制最大宽度为父容器宽度 */
  box-sizing: border-box; /* 确保padding不增加总宽度 */
}
/* 图片容器样式 */
.card-content img {
  display: block;
  margin: 12px auto;
  width: 100% !important;
  height: auto;
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.card-content :deep(.embedded-video-card) {
  margin: 12px 0;
  border: 1px solid #e8ebf7;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.card-content :deep(.embedded-video-player) {
  display: block;
  width: 100%;
  max-height: 100%;
  background: #000;
}

.card-content :deep(.embedded-video-meta) {
  padding: 10px 12px;
}

.card-content :deep(.embedded-video-title) {
  font-size: 14px;
  font-weight: 600;
  color: #222;
  margin-bottom: 4px;
}

.card-content :deep(.embedded-video-description) {
  font-size: 12px;
  color: #666;
  line-height: 1.5;
}
.loading-message {
  width: 100%;
  /* min-height: 60px; */
  display: flex;
  align-items: center;
  margin: 0;
}
.card-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-top: 1px solid #f4f4f4;
  margin: 0 0.5rem;
  box-sizing: border-box;
}
.card-actions .actions-left {
  display: flex;
  gap: 0.5rem;
}
.action-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  transition: all 0.2s ease;
}
.action-btn img {
  widows: 20px;
  height: 20px;
}

.action-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 建议输入框样式 */
.advice-input-container {
  margin: 0.5rem;
  box-sizing: border-box;
  border: 1px solid #3b6ce3;
  border-radius: 4px;
  overflow: hidden;
  background-color: #f5f5f5;
}

.advice-input-wrapper {
  position: relative;
  padding: 8px;
}

.advice-textarea {
  width: 100%;
  min-height: 60px;
  border: 1px solid #f5f5f5;
  border-radius: 4px;
  resize: none;
  font-size: 14px;
  line-height: 1.5;
  box-sizing: border-box;
  overflow-y: auto;
  font-family: inherit;
  background-color: #f5f5f5;
  color: #333;
  caret-color: #3b6ce3;
  padding-right: 20px;
}

.advice-textarea::placeholder {
  color: #999;
  white-space: pre-line;
  font-size: 14px;
}

.advice-send-btn {
  position: absolute; /* 绝对定位，依附父容器 */
  top: 20%; /* 垂直居中 */
  right: 8px; /* 距离父容器右侧8px */
  transform: translateY(-50%); /* 调整垂直居中 */
  width: 20px;
  height: 20px;
  background-color: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.advice-send-btn img {
  width: 13px;
  height: 10px;
  object-fit: contain; /* 确保图片不变形 */
}
.action-right {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}
.edit-btn,
.send-btn {
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  background-color: #fff;
  border: 1px solid #3b6ee3;
  color: #3b6ee3;
  white-space: nowrap; /* 禁止文字换行，宽度随单行内容扩展 */
  box-sizing: border-box; /* 确保 padding 和 border 不增加按钮总宽度 */
}

/* 加载状态 */
.loading-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem;
  color: #999;
  font-size: 0.9rem;
}
.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #3b6ee3;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translate(-50%, -10px);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* 适配iOS企业微信底部导航 */
@media (max-width: 768px) and (orientation: portrait) {
  .conversation-content {
    padding-bottom: calc(env(safe-area-inset-bottom) + 40px);
  }
}
</style>
